import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { Button } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { CollectionPayload, CollectionTypes } from '@src/types/Collection';
import { PointConfigEdit } from '@src/types/PointConfig';
import {
  DeviceListDeviceRefsAll,
  WidgetCreate,
  WidgetTypes,
} from '@src/types/Widget';
import { UserRole } from '@src/services/auth/roles';
import { isSuperAdmin } from '@src/services/auth';
import storeConnector from '@store/storeConnector';
import React, { Dispatch, SetStateAction } from 'react';
import collectionSx from './configs/CollectionSx';
import GenerateDevicesButton from './Devices/GenerateDevicesButton';
import CreateDeviceButton from './Devices/CreateDeviceButton';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

interface ToolbarProps {
  collectionType?: CollectionTypes;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
  setOpenCreate?: Dispatch<SetStateAction<boolean>>;
  onSyncInflux?: () => void;
  isSyncing?: boolean;
  role: UserRole;
}

const baseAddPointConfig: PointConfigEdit = {
  pointConfigs: [
    {
      pointAlias: '',
      deviceType: '',
      displayName: '',
      pointName: '',
      dataType: '',
      isSiteControllerSourceDevicePointConfig: false,
      decimalPrecision: 0,
      scalingFactor: 1,
      invertDisplay: false,
      unitOfMeasurement: '',
      displayDenominator: '',
      valueMappings: {},
    },
  ],
};

const baseAddWidgetConfig: WidgetCreate = {
  widgets: [
    {
      type: WidgetTypes.DEVICE_LIST,
      internalId: '',
      name: '',
      isSystemWidget: true,
      header: '',
      pointConfigRefs: [{ pointAlias: 'somePoint' }],
      deviceType: 'unit', // legacy name: 'category'
      deviceRefs: DeviceListDeviceRefsAll.ALL,
    },
  ],
};

function CollectionToolbar({
  collectionType,
  setOpen,
  setJsonPayload,
  setOpenCreate,
  onSyncInflux,
  isSyncing,
  role,
}: ToolbarProps): React.ReactElement {
  const handleOpenAdd = () => {
    const baseConfig =
      collectionType === CollectionTypes.POINT_CONFIGS
        ? baseAddPointConfig
        : baseAddWidgetConfig;
    setOpen(true);
    setJsonPayload(baseConfig);
  };

  return (
    <GridToolbarContainer sx={collectionSx.collectionToolbar}>
      <div>
        {collectionType === CollectionTypes.DEVICES && (
          <>
            <GenerateDevicesButton
              setJsonPayload={setJsonPayload}
              setOpen={setOpen}
            />
            <div style={{ marginLeft: '20px', display: 'inline-block' }}>
              <CreateDeviceButton
                setJsonPayload={setJsonPayload}
                setOpen={setOpenCreate || setOpen}
              />
            </div>
          </>
        )}
        {(collectionType === CollectionTypes.POINT_CONFIGS ||
          collectionType === CollectionTypes.WIDGETS) && (
          <>
            <Button startIcon={<AddIcon />} onClick={handleOpenAdd}>
              <FormattedAlias id='Add config' />
            </Button>
            {collectionType === CollectionTypes.POINT_CONFIGS &&
              onSyncInflux &&
              isSuperAdmin(role) && (
                <Button
                  startIcon={
                    <SyncIcon
                      sx={{
                        animation: isSyncing
                          ? 'spin 1s linear infinite'
                          : 'none',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        },
                      }}
                    />
                  }
                  onClick={onSyncInflux}
                  disabled={isSyncing}
                  sx={{ ml: 1 }}
                >
                  <FormattedAlias id='Sync point configs from Influx' />
                </Button>
              )}
          </>
        )}
      </div>

      <div>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <GridToolbarQuickFilter
          sx={collectionSx.collectionToolbar.quickFilter}
        />
      </div>
    </GridToolbarContainer>
  );
}

export default storeConnector(CollectionToolbar, {
  user: ['role'],
});
